import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function formatDateTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toString()
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount)
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return str.slice(0, length) + '...'
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

export function getDomainFromUrl(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

export function getInitials(name: string | null): string {
  if (!name) return '?'
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'verified':
    case 'published':
    case 'completed':
    case 'active':
    case 'converted':
      return 'success'
    case 'pending':
    case 'processing':
    case 'new':
    case 'draft':
      return 'primary'
    case 'failed':
    case 'lost':
    case 'past_due':
    case 'canceled':
      return 'destructive'
    case 'archived':
      return 'muted'
    default:
      return 'muted'
  }
}

export function getStrategyLabel(strategy: string): string {
  const labels: Record<string, string> = {
    forsale: 'For Sale',
    landing: 'Landing Page',
    leadgen: 'Lead Generation',
    affiliate: 'Affiliate',
    microsite: 'Micro Site',
    content: 'News/Content Site',
  }
  return labels[strategy] || strategy
}

export function getStrategyDescription(strategy: string): string {
  const descriptions: Record<string, string> = {
    forsale: 'Professional domain for sale page with offer form',
    landing: 'High-converting landing page with lead capture',
    leadgen: 'Lead generation site with forms and CTAs',
    affiliate: 'Affiliate marketing page with product links',
    microsite: 'Content-focused site with SEO optimization',
    content: 'News & articles site optimized for AdSense monetization',
  }
  return descriptions[strategy] || ''
}

export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text)
}
